// web/src/routes/horses/edit.jsx
// Edit Horse — loads the horse, edits via the shared HorseForm, and PATCHes.
// PATCH is owner/manager-gated server-side; a client who reaches here will get
// a friendly "only owner or manager" message on save.

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import HorseForm from '../../components/horse/HorseForm';

export default function HorseEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['horse', id],
    queryFn: () => api.get(`/horses/${id}`),
  });

  const save = useMutation({
    mutationFn: (payload) => api.patch(`/horses/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['horse', id] });
      qc.invalidateQueries({ queryKey: ['horses'] });
      navigate(`/horses/${id}`);
    },
  });

  if (isLoading) return <div className="p-4 text-ink-400">Loading…</div>;
  if (isError || !data?.horse) {
    return (
      <div className="p-4">
        <p className="text-red-600">Horse not found.</p>
        <Link to="/horses" className="text-ink-800 underline">
          Back to horses
        </Link>
      </div>
    );
  }

  const h = data.horse;

  return (
    <div className="max-w-xl mx-auto p-4">
      <Link to={`/horses/${id}`} className="text-sm text-ink-500 hover:text-ink-800">
        ← {h.name}
      </Link>

      <h1 className="mt-2 mb-6 text-2xl font-semibold">Edit {h.name}</h1>

      <HorseForm
        mode="edit"
        initial={h}
        onSubmit={(payload) => save.mutate(payload)}
        submitting={save.isPending}
        submitError={save.isError ? friendlyError(save.error) : null}
      />

      <div className="mt-4">
        <Link to={`/horses/${id}`} className="text-sm text-ink-500 hover:text-ink-800">
          Cancel
        </Link>
      </div>
    </div>
  );
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message;
  switch (code) {
    case 'missing_name':
      return 'Please enter a name.';
    case 'invalid_sex':
      return 'Please choose a valid sex.';
    case 'no_fields':
      return 'Nothing to save.';
    case 'forbidden':
      return 'Only an owner or manager can edit horses.';
    case 'not_found':
      return 'That horse no longer exists.';
    case 'unauthorized':
    case 'session_expired':
      return 'Your session expired. Please sign in again.';
    default:
      return 'Something went wrong saving the horse. Please try again.';
  }
}
